/**
 * StaffSettings — Phase 7B. Same 4-tab UX as ParentSettings but tuned
 * for staff/admin/vendor roles:
 *
 *  1. Profile             — name, contact, role-specific fields
 *                            (employee ID, designation, department,
 *                            qualification, experience, joining date)
 *  2. Assignments         — read-only summary of franchise + branches
 *                            (and assigned classrooms for teachers)
 *  3. Notifications       — channel toggles + language
 *  4. Account & Security  — login email, password reset, status
 *
 * Backend contract is unchanged from P7A:
 *   PATCH /api/auth/me          — strict allow-list update
 *   GET   /api/auth/me          — fetch self
 *
 * NOTE on extra HRMS fields:
 * --------------------------
 * Fields like employee_id, designation, joining_date are stored on the
 * `staff` collection (separate from `users`) and surfaced via
 * /api/hrms/me/profile. We READ those for display but don't edit them
 * here — HR-controlled fields belong in the HRMS admin UI, not in
 * self-service. Self-service edits go through PATCH /auth/me only.
 *
 * Mounted at /app/profile for every non-family role.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  User, Bell, ShieldCheck, IdentificationCard, Buildings, EnvelopeSimple,
  Phone, ChatTeardropDots, FloppyDisk, X, Briefcase, FileText, Upload,
  Trash, CheckCircle, Warning,
} from "@phosphor-icons/react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Panel, Pill, Skeleton } from "@/components/Bits";
import PhoneInput from "@/components/PhoneInput";
import { NameInput } from "@/lib/validators";
import VerifyOtpModal from "@/components/VerifyOtpModal";

const TABS = [
  { key: "profile",      label: "Profile",            icon: User },
  { key: "employment",   label: "Assignments",        icon: Briefcase },
  { key: "documents",    label: "Documents (KYC)",    icon: FileText },
  { key: "notify",       label: "Notifications",      icon: Bell },
  { key: "security",     label: "Account & security", icon: ShieldCheck },
];

// Fields the backend treats as SENSITIVE — changes must clear an OTP
// challenge before they're applied (Phase 7C). Mirrors the server-side
// list in routers/auth.py.
const SENSITIVE_FIELDS = new Set([
  "phone", "whatsapp_phone", "alt_email",
  "emergency_contact", "pickup_permission",
]);

export default function StaffSettings() {
  const { user, refresh: refreshUser } = useAuth();
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState(null);
  const [hrmsProfile, setHrmsProfile] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [phoneValid, setPhoneValid] = useState(true);
  const [waPhoneValid, setWaPhoneValid] = useState(true);
  const [otpModal, setOtpModal] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      api.get("/auth/me"),
      api.get("/hrms/me/profile").catch(() => ({ data: null })),
    ]).then(([me, hr]) => {
      const p = me.value?.data || user;
      setProfile(p);
      setDraft(p);
      setHrmsProfile(hr.value?.data || null);
    });
  }, [user]);

  const dirty = useMemo(() => {
    if (!profile) return false;
    return Object.keys(draft).some(
      (k) => (draft[k] ?? null) !== (profile[k] ?? null)
    );
  }, [draft, profile]);

  // Compute the diff once — used both to detect sensitive changes (for
  // the OTP gate) and to ship to the backend.
  const computePatch = () => {
    const patch = {};
    Object.keys(draft).forEach((k) => {
      if ((draft[k] ?? null) === (profile[k] ?? null)) return;
      if (["role", "branch_id", "branch_ids", "franchise_id",
            "managed_branch_ids", "permissions", "is_active",
            "email", "user_id", "_id", "password_hash",
            "modified_at", "modified_by", "created_at"].includes(k)) return;
      patch[k] = draft[k] === "" ? null : draft[k];
    });
    return patch;
  };

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!phoneValid) {
      toast.error("Mobile number isn't valid yet.");
      return;
    }
    if (draft.whatsapp_phone && !waPhoneValid) {
      toast.error("WhatsApp number isn't valid.");
      return;
    }
    const patch = computePatch();
    if (Object.keys(patch).length === 0) {
      toast.info("Nothing to save.");
      return;
    }
    // ───────────── Phase 7C — OTP gate ─────────────
    // If any of the touched fields is in the SENSITIVE_FIELDS set we
    // need to confirm the user with an OTP first. The modal handles the
    // send/verify dance and calls `onConfirmed(otpCode)` once the user
    // has typed the correct code.
    const sensitiveChanged = Object.keys(patch).some((k) => SENSITIVE_FIELDS.has(k));
    if (sensitiveChanged) {
      setOtpModal({
        identifier: profile.email,
        channel: "email",
        title: "Confirm sensitive profile change",
        body: "We've sent a 6-digit code to your email. Enter it to save the changes to your contact / pickup details.",
        onConfirmed: async (code) => {
          await persist(patch, code);
        },
      });
      return;
    }
    await persist(patch);
  };

  const persist = async (patch, otpCode) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me",
        { ...patch, ...(otpCode ? { _otp_code: otpCode } : {}) });
      setProfile(data);
      setDraft(data);
      setOtpModal(null);
      toast.success("Profile updated.");
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setDraft(profile); toast.info("Changes reverted."); };

  if (!profile) return <Skeleton lines={6} />;

  return (
    <div className="pb-24 lg:pb-6" data-testid="staff-settings-page">
      <PageHeader
        eyebrow="Settings"
        title="Your profile"
        subtitle={
          profile.last_profile_update
            ? `Last updated ${new Date(profile.last_profile_update).toLocaleString()}`
            : "Keep your details current so colleagues and parents can reach you."
        }
      />

      <div className="inline-flex rounded-full bg-paper-alt p-1 border border-border mb-6 overflow-x-auto max-w-full" data-testid="settings-tabs">
        {TABS.map((t) => {
          const Ic = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 transition ${tab === t.key ? "bg-white shadow-sm text-ink" : "text-ink-soft hover:text-ink"}`}>
              <Ic size={14} weight="duotone" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "profile" && (
        <ProfileTab
          profile={profile}
          hrmsProfile={hrmsProfile}
          draft={draft}
          setField={setField}
          phoneValid={phoneValid}
          setPhoneValid={setPhoneValid}
          waPhoneValid={waPhoneValid}
          setWaPhoneValid={setWaPhoneValid}
        />
      )}
      {tab === "employment" && (
        <EmploymentTab profile={profile} hrmsProfile={hrmsProfile} />
      )}
      {tab === "documents" && (
        <DocumentsTab profile={profile} hrmsProfile={hrmsProfile} />
      )}
      {tab === "notify" && (
        <NotifyTab draft={draft} setField={setField} />
      )}
      {tab === "security" && (
        <SecurityTab profile={profile} />
      )}

      {dirty && ["profile", "notify"].includes(tab) && (
        <div className="fixed bottom-0 inset-x-0 lg:inset-x-auto lg:right-6 lg:bottom-6 lg:left-auto lg:w-auto z-30" data-testid="save-bar">
          <div className="bg-ink text-paper rounded-none lg:rounded-2xl px-5 py-3 flex items-center gap-3 shadow-2xl">
            <span className="text-sm flex-1 lg:flex-none">Unsaved changes</span>
            <button onClick={cancel} className="px-3 h-9 rounded-full border border-paper/30 text-sm" data-testid="cancel-btn">
              <X weight="bold" /> Cancel
            </button>
            <button onClick={save} disabled={saving}
              className="px-4 h-9 rounded-full bg-mustard text-ink font-semibold inline-flex items-center gap-1.5 text-sm disabled:opacity-50" data-testid="save-btn">
              <FloppyDisk weight="bold" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}

      {otpModal && (
        <VerifyOtpModal
          identifier={otpModal.identifier}
          channel={otpModal.channel}
          title={otpModal.title}
          body={otpModal.body}
          onCancel={() => setOtpModal(null)}
          onConfirmed={otpModal.onConfirmed}
        />
      )}
    </div>
  );
}

/* ───────────────── Profile tab ─────────────── */
function ProfileTab({ profile, hrmsProfile, draft, setField, phoneValid, setPhoneValid, waPhoneValid, setWaPhoneValid }) {
  return (
    <div className="space-y-4" data-testid="profile-tab">
      <Panel title="Identity">
        <div className="flex items-start gap-4 mb-4">
          <PhotoUpload value={draft.photo_url} onChange={(url) => setField("photo_url", url)} />
          <div className="flex-1">
            <Pill tone="mustard">{(profile.role || "").replace(/_/g, " ")}</Pill>
            {hrmsProfile?.employee_id && <Pill tone="sage" className="ml-2">EMP {hrmsProfile.employee_id}</Pill>}
            <div className="text-xs text-ink-soft mt-2">Your photo helps parents recognise you on pickup. JPG/PNG, ≤2 MB.</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Full name" required>
            <NameInput value={draft.name || ""} onChange={(v) => setField("name", v)}
              placeholder="e.g. Anjali Nair" testid="profile-name" />
          </Field>
          <Field label="Email (login)">
            <ReadOnly value={profile.email} testid="profile-email-readonly" />
          </Field>
        </div>
      </Panel>

      <Panel title="Contact">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Mobile number" required hint="Changing this needs an OTP confirmation.">
            <PhoneInput value={draft.phone || ""} onChange={(v) => setField("phone", v)}
              onValidityChange={setPhoneValid} placeholder="9844139333" testId="profile-phone" />
          </Field>
          <Field label="WhatsApp (if different)" hint="Changing this needs an OTP confirmation.">
            <PhoneInput value={draft.whatsapp_phone || ""} onChange={(v) => setField("whatsapp_phone", v)}
              onValidityChange={setWaPhoneValid} placeholder="9844139333" testId="profile-whatsapp" />
          </Field>
          <Field label="Alternate email" hint="Changing this needs an OTP confirmation.">
            <input type="email" className="input" value={draft.alt_email || ""}
              onChange={(e) => setField("alt_email", e.target.value)} placeholder="alt@example.com"
              data-testid="profile-alt-email" />
          </Field>
        </div>
      </Panel>

      <Panel title="Address">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Residential address" className="sm:col-span-2">
            <textarea rows={2} className="input" value={draft.address || ""}
              onChange={(e) => setField("address", e.target.value)}
              placeholder="e.g. 42, 5th Cross, Indiranagar 2nd Stage" data-testid="profile-address" />
          </Field>
          <Field label="City"><input className="input" value={draft.city || ""}
            onChange={(e) => setField("city", e.target.value)} placeholder="Bangalore" data-testid="profile-city" /></Field>
          <Field label="State"><input className="input" value={draft.state || ""}
            onChange={(e) => setField("state", e.target.value)} placeholder="Karnataka" data-testid="profile-state" /></Field>
          <Field label="Country"><input className="input" value={draft.country || "India"}
            onChange={(e) => setField("country", e.target.value)} data-testid="profile-country" /></Field>
          <Field label="Pincode / Postal code"><input className="input" value={draft.pincode || ""}
            onChange={(e) => setField("pincode", e.target.value)} placeholder="560038" data-testid="profile-pincode" /></Field>
        </div>
      </Panel>
    </div>
  );
}

/* ───────────────── Employment / Assignments tab ─────────────── */
function EmploymentTab({ profile, hrmsProfile }) {
  return (
    <div className="space-y-4" data-testid="employment-tab">
      <Panel title="Role & assignments">
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <Detail label="Role" value={(profile.role || "").replace(/_/g, " ")} />
          <Detail label="Sub-role / category" value={profile.sub_role || hrmsProfile?.category || "—"} />
          <Detail label="Employee ID" value={hrmsProfile?.employee_id || "—"} />
          <Detail label="Department" value={hrmsProfile?.department || "—"} />
          <Detail label="Designation" value={hrmsProfile?.designation || "—"} />
          <Detail label="Reporting manager" value={hrmsProfile?.reporting_manager_name || "—"} />
          <Detail label="Joining date" value={(hrmsProfile?.joining_date || "").slice(0, 10) || "—"} />
          <Detail label="Employment type" value={hrmsProfile?.employment_type || "—"} />
          <Detail label="Qualification" value={hrmsProfile?.qualification || "—"} />
          <Detail label="Total experience" value={hrmsProfile?.experience_years ? `${hrmsProfile.experience_years} yrs` : "—"} />
        </div>
        <p className="text-xs text-ink-soft mt-4">
          These details are managed by your HR admin. To request a change,
          please reach out to your Branch Admin or HR.
        </p>
      </Panel>

      <Panel title="Branches">
        <div className="text-sm">
          {(profile.branch_ids?.length || profile.branch_id) ? (
            <ul className="list-disc pl-5 space-y-1">
              {(profile.branch_ids || (profile.branch_id ? [profile.branch_id] : []))
                .map((id) => <li key={id}>{id}</li>)}
            </ul>
          ) : <Empty label="Not assigned to any branch." />}
        </div>
        {profile.franchise_id && (
          <div className="mt-3 text-sm">
            <span className="text-ink-soft">Franchise: </span>
            <span className="font-medium">{profile.franchise_id}</span>
          </div>
        )}
      </Panel>
    </div>
  );
}

/* ───────────────── Notifications tab ─────────────── */
function NotifyTab({ draft, setField }) {
  return (
    <Panel title="How should we reach you?" testid="notify-tab">
      <p className="text-sm text-ink-soft mb-4">Pick the channels you'd like to receive school + HR communications on.</p>
      <div className="space-y-3">
        <NotifyRow icon={<EnvelopeSimple size={18} weight="duotone" />} label="Email"
          checked={draft.notify_email !== false}
          onChange={(v) => setField("notify_email", v)} testid="notify-email" />
        <NotifyRow icon={<ChatTeardropDots size={18} weight="duotone" />} label="WhatsApp"
          checked={draft.notify_whatsapp !== false}
          onChange={(v) => setField("notify_whatsapp", v)} testid="notify-whatsapp" />
        <NotifyRow icon={<Phone size={18} weight="duotone" />} label="SMS"
          checked={!!draft.notify_sms} onChange={(v) => setField("notify_sms", v)} testid="notify-sms" />
        <NotifyRow icon={<Bell size={18} weight="duotone" />} label="In-app push"
          checked={draft.notify_in_app !== false}
          onChange={(v) => setField("notify_in_app", v)} testid="notify-inapp" />
      </div>
      <div className="mt-6">
        <Field label="Preferred language">
          <select className="input" value={draft.language || "en"}
            onChange={(e) => setField("language", e.target.value)} data-testid="notify-language">
            <option value="en">English</option>
            <option value="hi">Hindi</option>
            <option value="kn">Kannada</option>
            <option value="te">Telugu</option>
            <option value="ta">Tamil</option>
            <option value="mr">Marathi</option>
          </select>
        </Field>
      </div>
    </Panel>
  );
}

/* ───────────────── Account & Security tab ─────────────── */
function SecurityTab({ profile }) {
  const [resetting, setResetting] = useState(false);
  const sendReset = async () => {
    setResetting(true);
    try {
      await api.post("/auth/forgot-password", { identifier: profile.email, channel: "email" });
      toast.success("Password reset link sent to your email.");
    } catch { toast.error("Could not send reset link."); }
    finally { setResetting(false); }
  };
  return (
    <div className="space-y-4" data-testid="security-tab">
      <Panel title="Account">
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <Detail label="Login email" value={profile.email} />
          <Detail label="Role" value={(profile.role || "").replace(/_/g, " ")} />
          <Detail label="Status" value={profile.is_active === false ? "Deactivated" : "Active"} />
          <Detail label="Last login" value={profile.last_login ? new Date(profile.last_login).toLocaleString() : "—"} />
        </div>
      </Panel>
      <Panel title="Password">
        <p className="text-sm text-ink-soft mb-3">For security we send password resets via email. Click below to receive a secure link.</p>
        <button onClick={sendReset} disabled={resetting}
          className="px-5 h-11 rounded-full bg-terracotta text-white font-semibold disabled:opacity-50" data-testid="password-reset-btn">
          {resetting ? "Sending…" : "Send password reset link"}
        </button>
      </Panel>
    </div>
  );
}

/* ───────────────── micro components ─────────────── */
function PhotoUpload({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Photo must be ≤ 2 MB."); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Use the shared file-upload endpoint (already wired to object
      // storage + size/MIME validation). ``folder=profile_photos`` keeps
      // the bucket tidy.
      const { data } = await api.post("/files/upload?folder=profile_photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url || data.file_url);
      toast.success("Photo uploaded — click Save to apply.");
    } catch { toast.error("Could not upload photo."); }
    finally { setBusy(false); }
  };
  return (
    <label className="relative inline-block cursor-pointer">
      {value ? (
        <img src={value} alt="" className="w-20 h-20 rounded-2xl object-cover border-2 border-mustard" />
      ) : (
        <div className="w-20 h-20 rounded-2xl bg-paper-alt border-2 border-dashed border-border flex items-center justify-center text-ink-soft text-xs">+ Photo</div>
      )}
      <input type="file" accept="image/png,image/jpeg" hidden onChange={onPick} data-testid="profile-photo-upload" />
      {busy && <div className="absolute inset-0 rounded-2xl bg-white/70 flex items-center justify-center text-xs">…</div>}
    </label>
  );
}

function Field({ label, required, hint, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs text-ink-soft block mb-1">
        {label} {required && <span className="text-terracotta">*</span>}
      </span>
      {children}
      {hint && <span className="text-[10px] text-ink-soft block mt-1">{hint}</span>}
      <style>{`
        .input { width: 100%; height: 44px; padding: 0 12px; border-radius: 12px;
                 border: 1px solid #EBE5DF; background: #fff; outline: none;
                 font-family: 'DM Sans', sans-serif; font-size: 14px; }
        .input:focus { box-shadow: 0 0 0 2px rgba(229,107,85,0.4); }
        .input::placeholder { color: #BDB3A7; font-weight: 400; opacity: 1; }
        textarea.input { padding: 10px 12px; height: auto; }
      `}</style>
    </label>
  );
}

function ReadOnly({ value, testid }) {
  return (
    <div className="h-11 px-3 rounded-xl border border-border bg-paper-alt/60 flex items-center text-sm text-ink-soft" data-testid={testid}>
      {value} <span className="ml-2 text-xs">· read-only</span>
    </div>
  );
}

function NotifyRow({ icon, label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 p-3 rounded-xl border border-border cursor-pointer hover:bg-paper-alt/50 transition">
      <span className="text-terracotta">{icon}</span>
      <span className="flex-1 text-sm font-medium">{label}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 accent-terracotta" data-testid={testid} />
    </label>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-soft mb-0.5">{label}</div>
      <div className="font-medium break-words">{value}</div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="py-6 text-center text-sm text-ink-soft">{label}</div>;
}

// Phase 8 — Staff KYC document upload (PAN / Aadhaar / Address / Qual /
// Experience / Bank proof / Medical fitness / Police verification / Other).
// Maps to `documents` API with category="employee".
const KYC_DOC_TYPES = [
  { key: "pan",                       label: "PAN card",                            req: true },
  { key: "aadhaar",                   label: "Aadhaar (optional, voluntary)",       req: false },
  { key: "address_proof",             label: "Address proof",                       req: true },
  { key: "educational_certificate",   label: "Qualification certificate",           req: true },
  { key: "experience_certificate",    label: "Experience certificate",              req: false },
  { key: "bank_proof",                label: "Bank proof / Cancelled cheque",       req: true },
  { key: "medical_fitness",           label: "Medical fitness certificate",         req: false },
  { key: "police_verification",       label: "Police verification (if required)",   req: false },
  { key: "other",                     label: "Other supporting documents",          req: false },
];

function DocumentsTab({ profile, hrmsProfile }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const subjectId = profile?.user_id;   // documents API uses user_id for category=employee

  const load = async () => {
    if (!subjectId) return;
    const r = await api.get(`/documents?category=employee&subject_id=${subjectId}&limit=200`)
      .catch(() => ({ data: { items: [] } }));
    setDocs(r.data?.items || r.data || []);
  };
  useEffect(() => { load(); }, [subjectId]);

  const upload = async (type, file) => {
    if (!file || !subjectId) return;
    setUploading(type);
    try {
      // 1) Upload binary to /files
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "staff_kyc");
      const fr = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fileMeta = fr.data;
      // 2) Register the document row
      await api.post("/documents", {
        subject_kind: "employee",
        subject_id: subjectId,
        category: "employee",
        document_type: type,
        document_name: KYC_DOC_TYPES.find(d => d.key === type)?.label || type,
        file_id: fileMeta.file_id,
      });
      toast.success(`${type.replace(/_/g, " ")} uploaded`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(null); }
  };

  const remove = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${docId}`);
    toast.success("Deleted");
    load();
  };

  const byType = (k) => docs.filter(d => d.document_type === k);
  const requiredMissing = KYC_DOC_TYPES.filter(t => t.req && byType(t.key).length === 0).length;

  return (
    <Panel
      title="KYC / Staff documents"
      testid="kyc-panel"
      action={requiredMissing > 0
        ? <Pill tone="terracotta"><Warning size={10} weight="bold" className="mr-1" />{requiredMissing} required missing</Pill>
        : <Pill tone="sage"><CheckCircle size={10} weight="bold" className="mr-1" />All required uploaded</Pill>}
    >
      <div className="text-xs text-ink-soft mb-4 p-3 bg-paper-alt rounded-2xl border border-border">
        Upload original-quality scans (PDF or JPG, &lt; 5MB per file). All documents
        are encrypted at rest and only your branch HR + super admin can view them.
        DigiLocker / Aadhaar OCR is intentionally NOT used — uploads are manual.
      </div>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="kyc-grid">
        {KYC_DOC_TYPES.map(t => {
          const items = byType(t.key);
          const isUploading = uploading === t.key;
          return (
            <div key={t.key} className="bg-white border border-border rounded-2xl p-4" data-testid={`kyc-${t.key}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div>
                  <div className="font-semibold text-sm flex items-center gap-1.5">
                    <FileText size={14} weight="duotone" className="text-mango" />
                    {t.label}
                  </div>
                  {t.req && items.length === 0 && (
                    <div className="text-[10px] text-terracotta font-semibold mt-0.5">Required</div>
                  )}
                </div>
                <Pill tone={items.length > 0 ? "sage" : "ink"}>{items.length}</Pill>
              </div>
              {items.length > 0 ? (
                <div className="space-y-1.5 mb-3">
                  {items.map(d => (
                    <div key={d.document_id} className="flex items-center justify-between gap-2 text-xs bg-paper-alt rounded-xl px-2.5 py-1.5">
                      <a
                        href={d.file_id ? `${API_BASE}/files/${d.file_id}/download` : "#"}
                        target="_blank" rel="noreferrer"
                        className="truncate hover:underline text-cobalt-deep flex-1"
                      >
                        {d.document_name || d.document_type}
                      </a>
                      <Pill tone={d.status === "verified" ? "sage" : d.status === "rejected" ? "terracotta" : "mustard"}>{d.status?.replace("_", " ") || "submitted"}</Pill>
                      <button onClick={() => remove(d.document_id)} className="p-1 rounded hover:bg-white" data-testid={`kyc-delete-${d.document_id}`} title="Delete">
                        <Trash size={11} weight="bold" className="text-terracotta" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              <label className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-2xl border border-dashed text-xs font-semibold cursor-pointer hover:bg-paper-alt ${isUploading ? "opacity-50" : ""}`} data-testid={`kyc-upload-${t.key}`}>
                <Upload size={12} weight="bold" />
                {isUploading ? "Uploading…" : "Upload"}
                <input type="file" hidden disabled={isUploading} accept="application/pdf,image/*" onChange={e => upload(t.key, e.target.files[0])} />
              </label>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

